import { Modal, Form, Button, Divider, Spin, Select } from "antd";
import "./style.scss";
import GeneralDetails from "./GeneralDetails";
import LocationDetails from "./LocationDetails";
import VisitDetails from "./VisitDetails";
import Logistics from "./Logistics";
import ResourcesDetails from "./ResourcesDetails";
import TeamDetails from "./TeamDetails";
import Postponement from "./Postponement";
import { useEffect, useState } from "react";
import { httpService } from "../../../../../services/httpService.service";
import { useSiteVisitStore } from "./store/siteVisitStore";
import cancelX from "../../../../../Assets/svg/cancelXbutton.svg";
import Done from "../../../../../Assets/svg/done.svg";
import exclamationMark from "../../../../../Assets/svg/exclamationMark.svg";
import showConfirm from "../../../../components/ExitConfirm/ExitConfirm";
import dayjs from "dayjs";
import moment from "moment";
import { siteVisitStatusEnum } from "../../../../../services/Enums/siteVisitStatusEnum";
import CatalogQuickAdd from "./CatalogQuickAdd";
import AddFacilityModal from "../../Facilities/AddFacilityModal";
import {
  hasCoordinates,
  resolveAddress,
  resolveState,
} from "./locationFallback";

const { Option } = Select;

// The statuses this modal offers. A source card further along (data
// collection, completed...) starts its duplicate back at pre-planning.
const DUPLICABLE_STATUSES = [2, 3];

// The source card's stored location, in the shape getAddress produces. Seeding
// it means the duplicate is savable straight away: LocationDetails only
// geocodes when the coordinates *change*, so duplicating the same card twice
// used to leave the address empty and block the save.
const addressFromLocation = (location) => {
  if (!location) return "";
  const lat = parseFloat(location.latitude);
  const lng = parseFloat(location.longitude);
  if (isNaN(lat) || isNaN(lng)) return "";
  const optional = {
    houseNumber: location.houseNumber,
    road: location.road,
    city: location.city,
    municipality: location.municipality,
    district: location.district,
    postcode: location.postcode,
    country: location.country,
    countryCode: location.countryCode,
    parish: location.address,
    state: location.state,
  };
  return {
    Latitude: lat,
    Longitude: lng,
    ...Object.fromEntries(
      Object.entries(optional).filter(([, value]) => value)
    ),
  };
};

// Duplicate Site Visit — front-end driven duplication.
// Loads the source card via getSiteVisit?id= (same prefill as Edit) so every
// field is editable, then submits through createSiteVisitCard (same payload as
// Add). Only the fields required to CREATE a new card are sent — no Id and no
// identifier, so the backend mints a fresh site visit card.
const DuplicateSiteVisitCardModal = ({
  visible,
  onCancel,
  onOk,
  siteVisitCardId,
  setMessage,
}) => {
  const [form] = Form.useForm();
  const [accuracy, setAccuracy] = useState(0);
  const [state, setState] = useState("");
  const [kills, setKills] = useState(0);
  const [projectId, setProjectId] = useState("");
  const [contractId, setContractId] = useState("");
  const [submitLoader, setSubmitLoader] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [selectedSectorId, setSelectedSectorId] = useState("");
  const [clusters, setClusters] = useState([]);
  const [singleTeam, setSingleTeam] = useState({});
  const [allIPPlans, setAllIPPlans] = useState([]);
  const [requiredPhotos, setRequiredPhotos] = useState(false);
  const [requiredVideos, setRequiredVideos] = useState(false);
  const [postponed, setPostponed] = useState(false);
  const [observationList, setObservationList] = useState(false);
  const [siteContactsRequired, setSiteContactsRequired] = useState(false);
  const [selectedIpPlan, setSelectedIpPlan] = useState([]);
  const [showHideIpPlanDetails, setShowHideIpPlanDetails] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [parishCheck, setParishCheck] = useState(false);
  const [address, setAddress] = useState("");
  const { setOtherDocuments } = useSiteVisitStore();
  const [filesToSave, setFilesToSave] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [duplicatedFiles, setDuplicatedFiles] = useState([]);
  const [addedCustomForms, setAddedCustomForms] = useState([]);
  const [facility, setFacility] = useState([]);
  const [sourceFacility, setSourceFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteLoader, setSiteLoader] = useState(false);

  // Keep the source card's facility selectable (and shown by name) even when
  // the facility list does not include it.
  const facilityOptions =
    sourceFacility?.id && !facility.some((item) => item.id === sourceFacility.id)
      ? [...facility, sourceFacility]
      : facility;

  const facilityData = async () => {
    try {
      httpService.get(
        "/api/Facility",
        (res) => {
          setFacility(res.data);
          setLoading(false);
        },
        () => {
          setLoading(false);
        }
      );
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const projectId = localStorage.getItem("currentProjectID");
    setProjectId(projectId);
    const contractId = localStorage.getItem("currentContractId");
    setContractId(contractId);
    facilityData();
  }, []);

  const fetchIpPlanDetails = (ipPlanId) => {
    if (ipPlanId) {
      httpService.get(
        `/api/IPPlan/getIPPlan?id=${ipPlanId}`,
        (res) => {
          setSelectedIpPlan(res.data);
        },
        () => {}
      );
    }
  };

  const fetchSectorData = async (clusterId) => {
    if (!clusterId) return;
    httpService.get(
      "/api/Site/getAllSectorsByCluster?clusterId=" + clusterId,
      (res) => setSectors(res.data)
    );
  };

  // Load the source card and prefill the form (mirrors EditSiteVisitCardModal),
  // but intentionally omits the identifier so the duplicate gets a new one.
  useEffect(() => {
    if (visible && siteVisitCardId) {
      setSiteLoader(true);
      httpService.get(
        `/api/Site/getSiteVisit?id=${siteVisitCardId}`,
        (res) => {
          setSiteLoader(false);

          const siteContact =
            res?.data?.visit?.contacts?.find(
              (contact) => contact?.contactType === 1
            ) || null;

          const pointContact =
            res?.data?.visit?.contacts?.find(
              (contact) => contact?.contactType === 2
            ) || null;

          const KIIContacts =
            res?.data?.visit?.contacts?.filter((e) => e?.contactType === 3) ||
            [];

          const KIIContactsLength = KIIContacts?.length;

          const plannedVisit = res?.data?.visit?.plannedVisit;
          setKills(KIIContactsLength || 0);
          const parishChecked = res?.data?.perish === "false" ? false : true;
          setParishCheck(parishChecked);
          setSiteContactsRequired(!!siteContact);
          const ipPlanId = res?.data?.ipPlan?.id;
          const clusterId = res?.data?.cluster?.id;

          const accuracyValue = res?.data?.locationDetails?.accuracy;
          const latitude = res?.data?.locationDetails?.latitude;
          const longitude = res?.data?.locationDetails?.longitude;

          const sourceStatus = res?.data?.siteVisitStatus;

          const initialData = {
            name: res?.data?.name,
            siteVisitStatus: DUPLICABLE_STATUSES.includes(sourceStatus)
              ? sourceStatus
              : DUPLICABLE_STATUSES[0],
            facilityType: res?.data?.facility?.id,
            city: res?.data?.locationDetails?.city,
            siteContactsRequired: !!siteContact,
            siteContacts: siteContact
              ? {
                  ...siteContact,
                  id: siteContact.id,
                  contactName: siteContact.name,
                }
              : {},
            pointContacts: pointContact
              ? {
                  ...pointContact,
                  id: pointContact.id,
                  contactName: pointContact.name,
                }
              : {},
            KIIcontacts: KIIContacts?.map((e) => ({
              ...e,
              contactName: e.name,
            })),
            parishCheck: parishChecked,
            parish:
              res?.data?.perish !== "false" ? res?.data?.perish : undefined,
            IPlaningCard: ipPlanId,
            cluster: clusterId,
            sector: res?.data?.sector?.id,
            subSector: res?.data?.subSector?.id,
            projectId: projectId,
            latitude: latitude,
            longitude: longitude,
            accuracy: accuracyValue || 0,
            isVirtual: res?.data?.isVirtual,
            dateOfPlannedVisit: plannedVisit ? dayjs(plannedVisit) : undefined,

            ObservationListCheck: res?.data?.observationList,
            beneficiariesCount:
              res?.data?.visit?.interviewDetails?.beneficiariesCount,
            kills: KIIContactsLength || 0,
            observationChecklistCount:
              res?.data?.visit?.interviewDetails?.observationChecklist,
            observationChecklistType:
              res?.data?.visit?.interviewDetails?.observationChecklistType ||
              "",

            mediaLimit: res?.data?.visit?.interviewDetails?.mediaLimit,
            samplingInstructions:
              res?.data?.visit?.interviewDetails?.samplingInstructions,
            clientInstructions:
              res?.data?.visit?.interviewDetails?.clientInstructions,
            otherInstructions:
              res?.data?.visit?.interviewDetails?.otherInstructions,
            visitPlanApproval: res?.data?.visit?.visitPlanApproval,

            specificsOfOnSiteOperation: res?.data?.specificsOfOnSiteOperation,

            tpmCoordinator: res?.data?.logistic?.tpmCoordinator?.id,
            coordinationType: res?.data?.logistic?.coordinationType,
            fulcrumAccount: res?.data?.logistic?.fulcrumAccount || "",
            activationDate: res?.data?.logistic?.activationDate,
            isTeamLocal: res?.data?.logistic?.isTeamLocal,
            state: res?.data?.locationDetails?.state,
            team: res?.data?.logistic?.team?.id,
            isPostponed: res?.data?.logistic?.isPostponed,
            postponedReason: res?.data?.logistic?.postponedReason,
            postponedDetails: res?.data?.logistic?.postponedDetails,
            notes: res?.data?.logistic?.notes,

            videosRequired: res?.data?.hasVideos || false,
            videosLimit: res?.data?.videoCount || 0,
            photosRequired: res?.data?.hasPhotos || false,
            photosLimit: res?.data?.photoCount || 0,
          };

          const customProperties =
            res?.data?.dataChecks?.map((item) => ({
              id: item.id,
              name: item.tool,
              quantity: item.target ?? 0,
              conducted: item.conducted ?? 0,
              isChecked: item.target > 0,
            })) || [];

          setAddedCustomForms(customProperties);
          setSourceFacility(res?.data?.facility || null);
          setAddress(addressFromLocation(res?.data?.locationDetails));
          setAccuracy(accuracyValue || 0);
          setState(res?.data?.locationDetails?.state || "");
          setSelectedIpPlan(ipPlanId);
          fetchIpPlanDetails(ipPlanId);
          fetchSectorData(clusterId);
          setPostponed(res?.data?.logistic?.isPostponed);
          setObservationList(res?.data?.observationList || false);
          setSelectedSectorId(res?.data?.sector?.id);
          setRequiredPhotos(res?.data?.hasPhotos || false);
          setRequiredVideos(res?.data?.hasVideos || false);

          const teamId = res?.data?.logistic?.team?.id;
          if (teamId) {
            httpService.get(
              `/api/User/getTeamById?teamId=${teamId}`,
              (teamRes) => {
                setSingleTeam(teamRes?.data);
              },
              () => {}
            );
          }

          // Carry over the attached documents so the duplicate keeps them.
          // These reference the same fileKeys and are sent in the create payload.
          const files = res?.data?.siteVisitFiles || [];
          setDuplicatedFiles([...files]);

          setInitialValues(initialData);
          form.setFieldsValue(initialData);
        },
        () => {
          setSiteLoader(false);
        }
      );
    }
  }, [visible, form, siteVisitCardId]);

  const formatDate = (date, type) => {
    if (!date?.$isDayjsObject) return "";

    if (type) {
      const newDate = new Date(date);
      const formattedDate = moment(
        new Date(newDate.setDate(newDate.getDate() - 1))
      );
      return formattedDate.format("YYYY-MM-DD HH:mm:ss");
    } else {
      const formattedDate = date.format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    }
  };

  const handleLeave = () => {
    onCancel();
    form.resetFields();
    setOtherDocuments([]);
    setSingleTeam(null);
    setObservationList(false);
    setKills(0);
    setClusters([]);
    setSectors([]);
    setSelectedIpPlan("");
    setShowHideIpPlanDetails(false);
    setAddress("");
    setFilesToSave([]);
    setDuplicatedFiles([]);
    setAddedCustomForms([]);
    setSelectedSectorId("");
    setSourceFacility(null);
  };

  const handleReturn = () => {};

  const onClose = () => {
    showConfirm(handleLeave, handleReturn);
  };

  const scrollToField = (errorInfo) => {
    const firstErrorField = errorInfo?.errorFields[0];
    if (firstErrorField) {
      form.scrollToField(firstErrorField?.name[0], {
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // Submits through createSiteVisitCard — the exact payload shape used when
  // adding a brand new card (create contacts / data checks / files, no Id).
  const handleSubmit = () => {
    setSubmitLoader(true);
    form
      .validateFields()
      .then((values) => {
        if (!hasCoordinates(address)) {
          setMessage({
            trigger: true,
            type: "danger",
            icon: exclamationMark,
            title: "Validation Error",
            text: "Please pick a location on the map, or enter a latitude and longitude.",
          });
          setTimeout(() => {
            setMessage({ trigger: false });
          }, 1000);
          setSubmitLoader(false);
          return;
        }

        const plannedVisit =
          values.dateOfPlannedVisit && `${formatDate(values.dateOfPlannedVisit)}`;
        const activationDate =
          values.dateOfPlannedVisit &&
          formatDate(values.dateOfPlannedVisit, "activation");

        let siteContacts = values.siteContacts ? [values.siteContacts] : [];
        let pointContacts = values.pointContacts ? [values.pointContacts] : [];
        let KIIcontacts = Array.isArray(values.KIIcontacts)
          ? values.KIIcontacts
          : [];

        siteContacts?.map((contact) => (contact.contactType = 1));
        pointContacts?.map((contact) => (contact.contactType = 2));
        KIIcontacts?.map((contact) => (contact.contactType = 3));

        const createContacts = [
          ...siteContacts,
          ...pointContacts,
          ...KIIcontacts,
        ];

        const formData = new FormData();
        values?.name && formData.append("Name", values.name);
        formData.append("IPPlanId", values.IPlaningCard);
        formData.append("ClusterId", values.cluster);
        formData.append("SectorId", values.sector);
        formData.append("SubSectorId", values.subSector || "");
        formData.append("SiteVisitStatus", values?.siteVisitStatus);
        values?.facilityType &&
          formData.append("FacilityId", values?.facilityType);
        formData.append("ContractId", contractId);
        formData.append("ProjectId", projectId);

        address?.Latitude &&
          formData.append("LocationDetails.Latitude", address.Latitude);
        address?.Longitude &&
          formData.append("LocationDetails.Longitude", address.Longitude);
        values?.accuracy &&
          formData.append("LocationDetails.Accuracy", values.accuracy);
        address?.houseNumber &&
          formData.append("LocationDetails.HouseNumber", address.houseNumber);
        address?.road && formData.append("LocationDetails.Road", address.road);
        address?.city && formData.append("LocationDetails.City", address.city);
        address?.municipality &&
          formData.append("LocationDetails.Municipality", address.municipality);
        address?.district &&
          formData.append("LocationDetails.District", address.district);
        address?.postcode &&
          formData.append("LocationDetails.Postcode", address.postcode);
        address?.country &&
          formData.append("LocationDetails.Country", address.country);
        address?.countryCode &&
          formData.append("LocationDetails.CountryCode", address.countryCode);
        const addressToSend = resolveAddress(address);
        addressToSend &&
          formData.append("LocationDetails.Address", addressToSend);
        // The map's own state, else what the user typed, else the state the card
        // (or its IP plan) already carries, else the closest place found.
        const stateToSend = resolveState({
          address,
          manualState: form.getFieldValue("manualState"),
          currentState: state,
          selectedIpPlan,
        });
        if (stateToSend) {
          formData.append("LocationDetails.State", stateToSend);
        }

        formData.append("Perish", values.parish || false);
        formData.append("isVirtual", values.isVirtual || false);
        formData.append("Visit.PlannedVisit", plannedVisit);

        createContacts?.forEach((contact, index) => {
          formData.append(
            `visit.createContacts[${index}].name`,
            contact.contactName
          );
          contact.email &&
            formData.append(
              `visit.createContacts[${index}].email`,
              contact.email
            );
          formData.append(`visit.createContacts[${index}].role`, contact.role);
          formData.append(
            `visit.createContacts[${index}].phone`,
            contact.phone || ""
          );
          formData.append(
            `visit.createContacts[${index}].skype`,
            contact.skype || ""
          );
          formData.append(
            `visit.createContacts[${index}].organization`,
            contact.organization || ""
          );
          formData.append(
            `visit.createContacts[${index}].contactType`,
            contact.contactType
          );
        });

        formData.append("Visit.ObservationListCheck", false);
        formData.append("ObservationList", values.ObservationListCheck || false);

        addedCustomForms?.forEach((item, index) => {
          formData.append(
            `CreateSiteVisitDataCheck[${index}].dataCheckId`,
            item.id
          );
          formData.append(
            `CreateSiteVisitDataCheck[${index}].target`,
            item.quantity
          );
          formData.append(
            `CreateSiteVisitDataCheck[${index}].isSelected`,
            item.isChecked
          );
        });

        formData.append(
          "Visit.CreateInterviewDetails.ObservationChecklist",
          values.observationChecklistCount || 0
        );
        formData.append(
          "Visit.CreateInterviewDetails.ObservationChecklistType",
          values.observationChecklistType || ""
        );
        formData.append(
          "Visit.CreateInterviewDetails.BeneficiariesCount",
          values.beneficiariesCount || 0
        );
        formData.append(
          "Visit.CreateInterviewDetails.ClientsCount",
          values.kills || 0
        );
        formData.append("Visit.CreateInterviewDetails.MediaLimit", 1);
        formData.append(
          "Visit.CreateInterviewDetails.SamplingInstructions",
          values.samplingInstructions || ""
        );
        formData.append(
          "Visit.CreateInterviewDetails.ClientInstructions",
          values.clientInstructions || ""
        );
        formData.append(
          "Visit.CreateInterviewDetails.OtherInstructions",
          values.otherInstructions || ""
        );
        formData.append(
          "Visit.VisitPlanApproval",
          values.visitPlanApproval || ""
        );

        values?.specificsOfOnSiteOperation &&
          formData.append(
            "SpecificsOfOnSiteOperation",
            values?.specificsOfOnSiteOperation
          );
        formData.append("ObservationList", values.observationList || false);

        formData.append("Logistic.TPMCoordinatorId", values.tpmCoordinator);
        formData.append("Logistic.CoordinationType", values.coordinationType);
        values.fulcrumAccount &&
          formData.append("Logistic.FulcrumAccount", values.fulcrumAccount);
        formData.append("Logistic.ActivationDate", activationDate);
        formData.append("Logistic.IsTeamLocal", values.isTeamLocal || false);
        formData.append("Logistic.TeamId", values.team);
        formData.append("Logistic.IsPostponed", values.isPostponed || false);
        formData.append(
          "Logistic.PostponedReason",
          values.postponedReason || ""
        );
        formData.append(
          "Logistic.PostponedDetails",
          values.postponedDetails || ""
        );
        // The notes field only renders while the card is postponed, so it is
        // missing from `values` otherwise; the form store still holds the
        // source card's notes.
        const notes = values.notes ?? form.getFieldValue("notes");
        notes && formData.append("Logistic.Notes", notes);

        formData.append("Tracking.SiteVisitStatus", 1);
        formData.append("Tracking.FieldTeamReportingStatus", 1);

        // Files: carried-over documents from the source card first, then any
        // newly uploaded ones. Both use the create Files[] contract.
        let fileIndex = 0;
        duplicatedFiles?.forEach((file) => {
          formData.append(`Files[${fileIndex}].fileName`, file?.fileName);
          formData.append(`Files[${fileIndex}].fileKey`, file?.fileKey);
          formData.append(`Files[${fileIndex}].fileSize`, file?.fileSize);
          formData.append(`Files[${fileIndex}].FileCategory`, 2);
          fileIndex++;
        });

        const newFiles = values.other_files || [];
        newFiles?.forEach((file) => {
          formData.append(`Files[${fileIndex}].fileName`, file?.name);
          formData.append(`Files[${fileIndex}].fileKey`, file?.fileKey);
          formData.append(`Files[${fileIndex}].fileSize`, file?.size);
          formData.append(`Files[${fileIndex}].FileCategory`, 2);
          fileIndex++;
        });

        httpService.post(
          "/api/Site/createSiteVisitCard",
          formData,
          () => {
            setMessage({
              trigger: true,
              type: "success",
              icon: Done,
              title: "Site visit card duplicated successfully!",
              text: "A new site visit card has been created from the duplicate and saved into the database!",
            });
            setTimeout(() => {
              setMessage({ trigger: false });
            }, 3000);
            setSubmitLoader(false);
            handleLeave();
            onOk();
          },
          (error) => {
            const errorMessage =
              error?.errors?.[0]?.message ||
              error?.message ||
              "Failed to duplicate site visit. Please try again later.";
            setSubmitLoader(false);
            setMessage({
              trigger: true,
              type: "danger",
              icon: exclamationMark,
              title: "Oops! A problem has occurred!",
              text: errorMessage,
            });
            setTimeout(() => {
              setMessage({ trigger: false });
            }, 3000);
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      })
      .catch((errorInfo) => {
        setSubmitLoader(false);
        scrollToField(errorInfo);
        setMessage({
          trigger: true,
          type: "danger",
          icon: exclamationMark,
          title: "Oops! A problem has occurred!",
          text:
            errorInfo.errorFields?.length > 0
              ? "Please fill all the required fields."
              : "An unexpected error occurred.",
        });
        setTimeout(() => {
          setMessage({ trigger: false });
        }, 3000);
      });
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable={false}
      width={800}
      footer={[]}
      title={
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            <img
              src={cancelX}
              style={{ marginLeft: "auto", cursor: "pointer" }}
              onClick={onClose}
              alt="cancelX"
            />
          </div>
          <h2 className="modalHeader">Duplicate Site Visit</h2>
        </>
      }
    >
      <Spin spinning={siteLoader}>
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <GeneralDetails
            form={form}
            onClose={onClose}
            selectedSectorId={selectedSectorId}
            setSelectedSectorId={setSelectedSectorId}
            clusters={clusters}
            setClusters={setClusters}
            allIPPlans={allIPPlans}
            setAllIPPlans={setAllIPPlans}
            sectors={sectors}
            setSectors={setSectors}
            selectedIpPlan={selectedIpPlan}
            setSelectedIpPlan={setSelectedIpPlan}
          />
          <Divider />
          <h3>Location details</h3>
          <LocationDetails
            form={form}
            isEditing={true}
            address={address}
            setAddress={setAddress}
            onClose={onClose}
            parishCheck={parishCheck}
            setParishCheck={setParishCheck}
            selectedIpPlan={selectedIpPlan}
            setSelectedIpPlan={setSelectedIpPlan}
            showHideIpPlanDetails={showHideIpPlanDetails}
            setShowHideIpPlanDetails={setShowHideIpPlanDetails}
            accuracy={accuracy}
            setAccuracy={setAccuracy}
            state={state}
          />
          <Divider />
          <h3>Visit details</h3>
          <VisitDetails
            form={form}
            kills={kills}
            setKills={setKills}
            requiredPhotos={requiredPhotos}
            setRequiredPhotos={setRequiredPhotos}
            requiredVideos={requiredVideos}
            setRequiredVideos={setRequiredVideos}
            siteContactsRequired={siteContactsRequired}
            setSiteContactsRequired={setSiteContactsRequired}
            observationList={observationList}
            setObservationList={setObservationList}
            addedCustomForms={addedCustomForms}
            setAddedCustomForms={setAddedCustomForms}
            onCancel={handleLeave}
          />
          <Divider />
          <h3>Logistics</h3>
          <Logistics />
          <Divider />
          <h3>Team</h3>
          <TeamDetails
            onCancel={onClose}
            singleTeam={singleTeam}
            setSingleTeam={setSingleTeam}
          />
          <Divider />
          <h3>Postponement</h3>
          <Postponement postponed={postponed} setPostponed={setPostponed} />
          <Divider />
          <Form.Item required label="Site Visit Status" name="siteVisitStatus">
            <Select
              placeholder="Select a progress status"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().startsWith(input.toLowerCase())
              }
            >
              {Object.entries(siteVisitStatusEnum)
                .filter(([key]) => key !== "default" && key > 1 && key < 4)
                .map(([key, value]) => (
                  <Option value={Number(key)}>{value.text}</Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Facility Type"
            name="facilityType"
            extra={
              <CatalogQuickAdd
                addLabel="New facility"
                manageLabel="Manage facilities"
                managePath="/project/planning?facilities"
                listPath="/api/Facility"
                createPath="/api/Facility"
                buildPayload={(values) => values}
                successText="Facility added"
                errorText="Failed to add facility"
                onCreated={(list, created) => {
                  setFacility(list);
                  created?.id &&
                    form.setFieldsValue({ facilityType: created.id });
                }}
                renderModal={(props) => <AddFacilityModal {...props} />}
              />
            }
          >
            <Select
              placeholder="Select a facility"
              allowClear
              showSearch
              loading={loading}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {facilityOptions.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Divider />
          <h3>Resources</h3>
          <ResourcesDetails
            form={form}
            existingFiles={duplicatedFiles}
            setExistingFiles={setDuplicatedFiles}
            filesToSave={filesToSave}
            setFilesToSave={setFilesToSave}
            setMessage={setMessage}
            setSubmitLoader={setSubmitLoader}
            setFilesToDelete={setFilesToDelete}
          />
          <Divider />
          <div className="container">
            <div className="row justify-content-center align-items-center g-3">
              <div className="col-12 col-md-4 col-lg-3 d-flex justify-content-center">
                <Button
                  className="inviteButtonTPM w-100"
                  key="back"
                  onClick={onClose}
                  loading={submitLoader}
                >
                  Cancel
                </Button>
              </div>
              <div className="col-12 col-md-4 col-lg-3 d-flex justify-content-center">
                <Button
                  className="addButtonTPM w-100"
                  key="submit"
                  type="primary"
                  loading={submitLoader}
                  onClick={handleSubmit}
                  style={{ minWidth: "fit-content" }}
                >
                  Duplicate
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default DuplicateSiteVisitCardModal;
